import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useToast } from '../components/useToast'

const ADMIN_USER_ID = '18af217b-614a-4f65-a471-518d82e70a1d' // el mismo UUID que usaste en las policies

export default function AdminSugerencias() {
  const navigate = useNavigate()
  const toast = useToast()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [suggestions, setSuggestions] = useState([])
  const [filter, setFilter] = useState('pending') // pending | approved | rejected | all
  const [selectedIds, setSelectedIds] = useState(new Set())

  // NUEVO: formulario de actualización
  const [updateContent, setUpdateContent] = useState('')
  const [publishingUpdate, setPublishingUpdate] = useState(false)

  useEffect(() => {
    async function init() {
      const { data } = await supabase.auth.getUser()
      if (!data?.user || data.user.id !== ADMIN_USER_ID) {
        navigate('/')
        return
      }
      setUser(data.user)
      await loadSuggestions('pending')
      setLoading(false)
    }
    init()
  }, [navigate])

  async function loadSuggestions(status) {
    let query = supabase.from('suggestions').select('*').order('created_at', { ascending: false })
    if (status !== 'all') query = query.eq('status', status)
    const { data, error } = await query
    if (error) {
      console.error(error)
      toast.error('Error', 'No se pudieron cargar las sugerencias')
      return
    }
    setSuggestions(data || [])
  }

  async function handleFilterChange(status) {
    setFilter(status)
    setSelectedIds(new Set())
    await loadSuggestions(status)
  }

  // NUEVO: publicar actualización oficial
  async function publishUpdate() {
    const content = updateContent.trim()
    if (!content) {
      toast.warning('Escribe el contenido de la actualización')
      return
    }
    setPublishingUpdate(true)
    const { error } = await supabase.from('suggestions').insert({
      content,
      status: 'approved',
      type: 'actualizacion',
      author_name: '📢 Actualización Calypso',
    })
    setPublishingUpdate(false)
    if (error) {
      console.error(error)
      toast.error('Error', 'No se pudo publicar la actualización')
      return
    }
    toast.success('Publicada', 'La actualización ya aparece de primeras en el muro')
    setUpdateContent('')
    await loadSuggestions(filter)
  }

  async function approve(id) {
    const { error } = await supabase.from('suggestions').update({ status: 'approved' }).eq('id', id)
    if (error) { toast.error('Error', 'No se pudo aprobar'); return }
    toast.success('Aprobada', 'La sugerencia ya está en el muro')
    setSuggestions(prev => prev.filter(s => s.id !== id))
  }

  async function reject(id) {
    const { error } = await supabase.from('suggestions').update({ status: 'rejected' }).eq('id', id)
    if (error) { toast.error('Error', 'No se pudo rechazar'); return }
    toast.success('Rechazada', 'La sugerencia fue rechazada')
    setSuggestions(prev => prev.filter(s => s.id !== id))
  }

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    if (selectedIds.size === suggestions.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(suggestions.map(s => s.id)))
    }
  }

  async function bulkApprove() {
    const ids = Array.from(selectedIds)
    const { error } = await supabase.from('suggestions').update({ status: 'approved' }).in('id', ids)
    if (error) { toast.error('Error', 'No se pudo aprobar en lote'); return }
    toast.success('Aprobadas', `${ids.length} sugerencia(s) ya están en el muro`)
    setSuggestions(prev => prev.filter(s => !selectedIds.has(s.id)))
    setSelectedIds(new Set())
  }

  async function bulkReject() {
    const ids = Array.from(selectedIds)
    const { error } = await supabase.from('suggestions').update({ status: 'rejected' }).in('id', ids)
    if (error) { toast.error('Error', 'No se pudo rechazar en lote'); return }
    toast.success('Rechazadas', `${ids.length} sugerencia(s) rechazadas`)
    setSuggestions(prev => prev.filter(s => !selectedIds.has(s.id)))
    setSelectedIds(new Set())
  }

  async function bulkDelete() {
    const ids = Array.from(selectedIds)
    const { error } = await supabase.from('suggestions').delete().in('id', ids)
    if (error) { toast.error('Error', 'No se pudo eliminar en lote'); return }
    toast.success('Eliminadas', `${ids.length} sugerencia(s) borradas`)
    setSuggestions(prev => prev.filter(s => !selectedIds.has(s.id)))
    setSelectedIds(new Set())
  }

  async function remove(id) {
    const { error } = await supabase.from('suggestions').delete().eq('id', id)
    if (error) { toast.error('Error', 'No se pudo eliminar'); return }
    toast.success('Eliminada', 'La sugerencia fue borrada')
    setSuggestions(prev => prev.filter(s => s.id !== id))
  }

  function timeAgo(iso) {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'Ahora mismo'
    if (mins < 60) return `Hace ${mins} min`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `Hace ${hrs}h`
    return `Hace ${Math.floor(hrs / 24)}d`
  }

if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20, background: 'var(--color-bg)' }}>
        <div style={{ position: 'relative', width: 78, height: 78, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'conic-gradient(from 0deg, #f7b500, #f59e0b, #6366f1, #f7b500)',
            animation: 'spin 1.4s linear infinite',
          }} />
          <div style={{
            position: 'absolute', inset: 4, borderRadius: '50%',
            background: 'var(--color-bg)',
          }} />
          <div style={{
            position: 'relative', width: 70, height: 70, borderRadius: '50%',
            background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 30, animation: 'pulse 1.5s infinite',
            boxShadow: '0 0 24px rgba(247,181,0,0.35)',
          }}>👑</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: 20, fontWeight: 700, marginBottom: 6,
            background: 'linear-gradient(135deg, #f7b500, #f59e0b)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>Panel de administración</div>
          <div style={{ fontSize: 14, color: 'var(--color-muted)' }}>Verificando acceso y cargando sugerencias...</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: '#f7b500', animation: `bounce 0.6s infinite ${i * 0.15}s` }} />
          ))}
        </div>
        <style>{`
          @keyframes pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.08);opacity:0.85} }
          @keyframes bounce { 0%,80%,100%{transform:translateY(0);opacity:0.5} 40%{transform:translateY(-8px);opacity:1} }
          @keyframes spin { to { transform: rotate(360deg) } }
        `}</style>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>

      {/* HEADER */}
      <header style={{ background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => navigate('/home')}
            style={{ width: 36, height: 36, background: 'rgba(120,120,120,0.08)', border: '1px solid var(--color-border)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 18, color: 'var(--color-text)', transition: '0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(-2px)'; e.currentTarget.style.borderColor = 'var(--color-primary)' }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'var(--color-border)' }}
          >←</button>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text)' }}>💡 Moderación de sugerencias</div>
            <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>Revisa, aprueba o rechaza las sugerencias enviadas por los usuarios</div>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px 80px' }}>

        {/* NUEVO: publicar actualización oficial */}
        <div style={{
          background: 'var(--color-primary-lt)',
          border: '1px solid var(--color-primary)',
          borderRadius: 16,
          padding: 16,
          marginBottom: 24,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: 'var(--color-text)' }}>
            📢 Publicar actualización
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 10 }}>
            Aparecerá siempre de primeras en el muro. La gente podrá comentarla y darle like igual que a las demás.
          </div>
          <textarea
            value={updateContent}
            onChange={e => setUpdateContent(e.target.value)}
            placeholder="Ej: Próximamente vamos a lanzar..."
            rows={3}
            style={{
              width: '100%', padding: 12, borderRadius: 10,
              border: '1px solid var(--color-border)', background: 'var(--color-surface)',
              color: 'var(--color-text)', fontSize: 13, resize: 'vertical',
              boxSizing: 'border-box', marginBottom: 10,
            }}
          />
          <button
            onClick={publishUpdate}
            disabled={publishingUpdate}
            style={{
              padding: '10px 16px', borderRadius: 10, border: 'none',
              background: 'var(--color-primary)', color: 'white',
              fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}
          >
            {publishingUpdate ? 'Publicando...' : '📢 Publicar actualización'}
          </button>
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <button
              key={f}
              onClick={() => handleFilterChange(f)}
              style={{
                padding: '8px 14px',
                borderRadius: 10,
                border: `1px solid ${filter === f ? 'var(--color-primary)' : 'var(--color-border)'}`,
                background: filter === f ? 'var(--color-primary-lt)' : 'var(--color-surface)',
                color: filter === f ? 'var(--color-primary)' : 'var(--color-text)',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {f === 'pending' ? '⏳ Pendientes' : f === 'approved' ? '✅ Aprobadas' : f === 'rejected' ? '❌ Rechazadas' : '📋 Todas'}
            </button>
          ))}
        </div>

        {/* Barra de selección masiva */}
        {suggestions.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-muted)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedIds.size === suggestions.length}
                onChange={toggleSelectAll}
              />
              Seleccionar todo
            </label>

            {selectedIds.size > 0 && (
              <>
                <span style={{ fontSize: 12, color: 'var(--color-muted)' }}>
                  {selectedIds.size} seleccionada(s)
                </span>
                <button onClick={bulkApprove}
                  style={{ padding: '6px 12px', borderRadius: 10, border: 'none', background: '#22c55e', color: 'white', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                >✅ Aprobar todas</button>
                <button onClick={bulkReject}
                  style={{ padding: '6px 12px', borderRadius: 10, border: 'none', background: '#f59e0b', color: 'white', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                >❌ Rechazar todas</button>
                <button onClick={bulkDelete}
                  style={{ padding: '6px 12px', borderRadius: 10, border: 'none', background: '#ef4444', color: 'white', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                >🗑️ Eliminar todas</button>
              </>
            )}
          </div>
        )}

        {/* Lista */}
        {suggestions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 10px', color: 'var(--color-muted)' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
            No hay sugerencias en esta categoría
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {suggestions.map(s => (
              <div
                key={s.id}
                style={{
                  background: 'var(--color-surface)',
                  border: selectedIds.has(s.id) ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                  borderRadius: 16,
                  padding: 16,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(s.id)}
                      onChange={() => toggleSelect(s.id)}
                    />
                    <span style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: s.status === 'pending' ? 'rgba(245,158,11,0.15)' : s.status === 'approved' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                      color: s.status === 'pending' ? '#f59e0b' : s.status === 'approved' ? '#22c55e' : '#ef4444',
                    }}>
                      {s.status}
                    </span>
                    {s.type === 'actualizacion' && (
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                        background: 'var(--color-primary)', color: 'white',
                      }}>📢 Actualización</span>
                    )}
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--color-muted)' }}>{timeAgo(s.created_at)}</span>
                </div>

                <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>{s.content}</div>

                <div style={{ display: 'flex', gap: 8 }}>
                  {s.status !== 'approved' && (
                    <button onClick={() => approve(s.id)}
                      style={{ flex: 1, padding: 8, borderRadius: 10, border: 'none', background: '#22c55e', color: 'white', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                    >✅ Aprobar</button>
                  )}
                  {s.status !== 'rejected' && (
                    <button onClick={() => reject(s.id)}
                      style={{ flex: 1, padding: 8, borderRadius: 10, border: 'none', background: '#f59e0b', color: 'white', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                    >❌ Rechazar</button>
                  )}
                  <button onClick={() => remove(s.id)}
                    style={{ flex: 1, padding: 8, borderRadius: 10, border: 'none', background: '#ef4444', color: 'white', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                  >🗑️ Eliminar</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}